import { derived, get, type Readable } from 'svelte/store';
import { BaseViewModel, type BaseViewModelInterface } from '$services';
import type { ProjectData } from '@localekey/types';
import type { TextValue } from '@shared/types';

export type ProjectSelectorModelInterface = {
	readonly currentProjectId: Readable<string | undefined>;
	readonly projects: Readable<TextValue[]>;

	selectProject(projectId?: string): Promise<void>;
	editProject(project: ProjectData): void;
	newProject(): void;

	startProjectsListener(): Promise<void>;
} & BaseViewModelInterface;
class ProjectSelectorModel
	extends BaseViewModel
	implements ProjectSelectorModelInterface
{
	async startProjectsListener(): Promise<void> {
		const teamId = await this.get$('authService', 'currentTeamId');
		if (!teamId) {
			return;
		}
		await this.execute(
			'projectService',
			'setCurrentTeamIdListener',
			teamId,
		);
	}

	async selectProject(projectId: string): Promise<void> {
		if (!projectId || get(this.currentProjectId) === projectId) {
			return;
		}
		await Promise.all([
			await this.execute(
				'projectService',
				'setCurrentProjectIdListener',
				projectId,
			),
			this.goToRoute({
				pathParameters: projectId,
				queryParameters: undefined,
				route: 'project',
			}),
		]);
	}

	async editProject(project: ProjectData): Promise<void> {
		await this.execute('projectService', 'setEditingProject', project);
		await this.execute('appService', 'setShowProjectFormDialog', true);
	}

	async newProject(): Promise<void> {
		await this.execute('projectService', 'setEditingProject');
		await this.execute('appService', 'setShowProjectFormDialog', true);
	}

	get projects(): Readable<TextValue[]> {
		return derived(
			this.getR('projectService', 'projects', undefined),
			($projects) => {
				if (!$projects) {
					return [];
				}
				return $projects.map((project) => {
					return {
						text: project.name,
						value: project.id,
					};
				});
			},
		);
	}

	get currentProjectId(): Readable<string | undefined> {
		return derived(
			this.getR('projectService', 'currentProject', undefined),
			($currentProject) => {
				return $currentProject?.id;
			},
		);
	}
}

export const getProjectSelectorModel = (): ProjectSelectorModelInterface =>
	new ProjectSelectorModel({
		className: 'ProjectSelectorModel',
	});
